import { QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "motion/react";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { ApiProvider } from "./api/context";
import { resolveDataMode } from "./api/mode";
import { useBranding } from "./api/queries";
import type { Industry, Role } from "./api/types";
import { AuthProvider } from "./auth/AuthContext";
import { ForcePasswordChange, RedirectIfSignedIn, RequireAuth, RequireIndustry, RequireRole, RequireTenant } from "./auth/guards";
import { AppFrame } from "./layout/AppFrame";
import { BackOfficeShell } from "./layout/BackOfficeShell";
import { ImpersonationBanner } from "./layout/ImpersonationBanner";
import { applyBrand } from "./lib/theme";
import { createServices } from "./services";
import { CenteredLoading } from "./ui/components/States";
import { DialogsProvider } from "./ui/components/Dialogs";
import { ToastProvider } from "./ui/components/Toast";

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const SignupPage = lazy(() => import("./pages/auth/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const ChangePasswordPage = lazy(() => import("./pages/auth/ChangePasswordPage"));
const CheckoutPage = lazy(() => import("./pages/checkout/CheckoutPage"));
const TillPage = lazy(() => import("./pages/till/TillPage"));
const SalesPage = lazy(() => import("./pages/sales/SalesPage"));
const InvoicePage = lazy(() => import("./pages/invoice/InvoicePage"));
const ProductsPage = lazy(() => import("./pages/products/ProductsPage"));
const CategoriesPage = lazy(() => import("./pages/categories/CategoriesPage"));
const InventoryPage = lazy(() => import("./pages/inventory/InventoryPage"));
const CustomersPage = lazy(() => import("./pages/customers/CustomersPage"));
const MpesaPage = lazy(() => import("./pages/mpesa/MpesaPage"));
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"));
const AuditLogPage = lazy(() => import("./pages/audit/AuditLogPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const QueuePage = lazy(() => import("./pages/queue/QueuePage"));
const FuelPage = lazy(() => import("./pages/fuel/FuelPage"));
const PlatformPage = lazy(() => import("./pages/system/PlatformPage"));
const ImpersonatePage = lazy(() => import("./pages/system/ImpersonatePage"));
const NotFound = lazy(() => import("./pages/system/NotFound"));
const KitPage = import.meta.env.DEV ? lazy(() => import("./pages/kit/KitPage")) : null;

const { mode, reset } = resolveDataMode();
if (reset) {
  // Drop ?mock=reset so a reload doesn't wipe the demo shop again.
  const u = new URL(window.location.href);
  u.searchParams.delete("mock");
  window.history.replaceState(null, "", u.toString());
}
const services = createServices({ mode, reset });

const ALL: readonly Role[] = ["ADMIN", "MANAGER", "CASHIER"];
const BACK: readonly Role[] = ["ADMIN", "MANAGER"];
const ADMIN: readonly Role[] = ["ADMIN"];

function Guarded({ roles, industry, children }: { roles: readonly Role[]; industry?: Industry; children: ReactNode }) {
  const inner = <RequireRole roles={roles}>{children}</RequireRole>;
  return industry ? <RequireIndustry industry={industry}>{inner}</RequireIndustry> : inner;
}

/** Applies the tenant's primary colour to the whole app (§3.1, §13.3). */
function BrandingSync() {
  const color = useBranding().data?.primaryColor ?? null;
  useEffect(() => applyBrand(color), [color]);
  return null;
}

function TenantArea() {
  return (
    <RequireAuth>
      <ForcePasswordChange>
        <RequireTenant>
          <AppFrame>
            <BrandingSync />
            <Suspense fallback={<CenteredLoading />}>
              <Outlet />
            </Suspense>
          </AppFrame>
        </RequireTenant>
      </ForcePasswordChange>
    </RequireAuth>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<CenteredLoading />}>
      <Routes>
        <Route path="/login" element={<RedirectIfSignedIn><LoginPage /></RedirectIfSignedIn>} />
        <Route path="/signup" element={<RedirectIfSignedIn><SignupPage /></RedirectIfSignedIn>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
        <Route path="/platform" element={<RequireAuth><PlatformPage /></RequireAuth>} />
        {KitPage && <Route path="/__kit" element={<KitPage />} />}

        <Route
          path="/invoice/:saleId"
          element={
            <RequireAuth>
              <ForcePasswordChange>
                <RequireTenant>
                  <Guarded roles={ALL}>
                    <div className="no-print">
                      <ImpersonationBanner />
                    </div>
                    <InvoicePage />
                  </Guarded>
                </RequireTenant>
              </ForcePasswordChange>
            </RequireAuth>
          }
        />
        <Route element={<TenantArea />}>
          <Route path="/" element={<Guarded roles={ALL}><CheckoutPage /></Guarded>} />
          <Route element={<BackOfficeShell />}>
            <Route path="/till" element={<Guarded roles={ALL}><TillPage /></Guarded>} />
            <Route path="/sales" element={<Guarded roles={ALL}><SalesPage /></Guarded>} />
            <Route path="/sales/queue" element={<Guarded roles={BACK}><QueuePage /></Guarded>} />
            <Route path="/customers" element={<Guarded roles={ALL}><CustomersPage /></Guarded>} />
            <Route path="/products" element={<Guarded roles={BACK}><ProductsPage /></Guarded>} />
            <Route path="/categories" element={<Guarded roles={BACK}><CategoriesPage /></Guarded>} />
            <Route path="/inventory" element={<Guarded roles={BACK}><InventoryPage /></Guarded>} />
            <Route path="/fuel" element={<Guarded roles={BACK} industry="PETROL_STATION"><FuelPage /></Guarded>} />
            <Route path="/mpesa" element={<Guarded roles={BACK}><MpesaPage /></Guarded>} />
            <Route path="/reports" element={<Guarded roles={BACK}><ReportsPage /></Guarded>} />
            <Route path="/settings" element={<Guarded roles={ADMIN}><SettingsPage /></Guarded>} />
            <Route path="/audit-log" element={<Guarded roles={ADMIN}><AuditLogPage /></Guarded>} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export function Providers({ services: s, children }: { services: ReturnType<typeof createServices>; children: ReactNode }) {
  return (
    <QueryClientProvider client={s.queryClient}>
      <ApiProvider value={s.api}>
        <AuthProvider>
          {/* Honour the OS "reduce motion" setting for every motion component (§16.4). */}
          <MotionConfig reducedMotion="user">
            <DialogsProvider>
              <ToastProvider>{children}</ToastProvider>
            </DialogsProvider>
          </MotionConfig>
        </AuthProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Providers services={services}>
        <AppRoutes />
      </Providers>
    </BrowserRouter>
  );
}
